/**
 * 首访欢迎信（2026-09-25 用户需求，03 模块《首访欢迎信与简介富文本_实现说明》需求 A）。
 * 三段式：信封态 → 拆封动画（封盖翻起 + 信纸抽出升起）→ 阅读态（公告纸面）。
 * - 触发：localStorage['chronospeak-letter-seen'] 无记录 且 存在已发布欢迎信公告。
 * - 与首访导览共存：信先、导览后（TownPage 的 guide-seen 逻辑不动，两 key 独立）。
 * - 关闭写记忆；Esc/「收下」等价；遮罩（信封态）点击不关（仪式感）；阅读态遮罩可关。
 * - a11y：role=dialog aria-modal、初始焦点落「拆封」、焦点圈定回归、双语 aria。
 * - reduced-motion：静置展开（跳过拆封动画直接阅读态）。
 * - 公告拉取失败/无欢迎信：组件整体不渲染，零影响。
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router';
import type { NoticeDetail } from '@chronospeak/contracts';
import type { Envelope } from '../../lib/api-types';
import { http } from '../../lib/http';
import { track, useLocale } from '../../lib/locale';

const LETTER_SEEN_KEY = 'chronospeak-letter-seen';
/** 拆封动画总长（封盖 .55s + 信纸抽出/升起衔接，实现说明 A3 允许微调到 1.2s 仪式感） */
const UNSEAL_MS = 1150;

type Stage = 'envelope' | 'unsealing' | 'reading';

export function WelcomeLetter() {
  const { locale } = useLocale();
  const [notice, setNotice] = useState<NoticeDetail | null>(null);
  const [checked, setChecked] = useState(false); // localStorage 检查完成
  const [stage, setStage] = useState<Stage>('envelope');
  const dialogRef = useRef<HTMLDivElement | null>(null);
  const unsealBtnRef = useRef<HTMLButtonElement | null>(null);
  const closeBtnRef = useRef<HTMLButtonElement | null>(null);
  const lastFocused = useRef<HTMLElement | null>(null);

  // ① 检查记忆 + 拉取欢迎信（失败静默——信件是增强非必达）
  useEffect(() => {
    let seen = false;
    try {
      seen = localStorage.getItem(LETTER_SEEN_KEY) === '1';
    } catch {
      seen = false; // 隐私模式等 localStorage 异常 → 视为未读（信件可再弹，无副作用）
    }
    if (seen) {
      setChecked(true);
      return;
    }
    let cancelled = false;
    http
      .get<Envelope<NoticeDetail | null>>(`/public/notices/welcome/current?locale=${locale}`)
      .then((res) => {
        if (cancelled) return;
        setNotice(res.data);
        setChecked(true);
        if (res.data) {
          lastFocused.current = document.activeElement as HTMLElement | null;
          track('welcome_letter_shown', { locale });
        }
      })
      .catch(() => {
        if (!cancelled) setChecked(true); // 拉取失败 → 不弹，一切照旧
      });
    return () => {
      cancelled = true;
    };
  }, [locale]);

  const dismissed = useCallback(
    (method: 'button' | 'esc') => {
      try {
        localStorage.setItem(LETTER_SEEN_KEY, '1');
      } catch {
        // 忽略——写不进则下次还会弹（可接受降级）
      }
      track('welcome_letter_dismissed', { method, locale });
      lastFocused.current?.focus?.();
      // 通知 TownPage 补弹首访导览（A3：信先、导览后）
      window.dispatchEvent(new CustomEvent('town-letter-dismissed'));
      setNotice(null); // 组件卸载，导览照常接管
    },
    [locale],
  );

  // ② 拆封：封盖翻起 → 信纸抽出升起 → 阅读态
  const unseal = useCallback(() => {
    if (stage !== 'envelope') return;
    setStage('unsealing');
    track('welcome_letter_opened', { locale });
    window.setTimeout(() => setStage('reading'), UNSEAL_MS);
  }, [stage, locale]);

  // ③ 阅读态初始焦点 + 焦点圈定 + Esc
  useEffect(() => {
    if (stage !== 'reading') return;
    closeBtnRef.current?.focus();
    const dialog = dialogRef.current;
    if (!dialog) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        dismissed('esc');
        return;
      }
      if (e.key !== 'Tab') return;
      const focusables = dialog.querySelectorAll<HTMLElement>('a[href], button:not([disabled])');
      if (!focusables.length) return;
      const first = focusables[0]!;
      const last = focusables[focusables.length - 1]!;
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [stage, dismissed]);

  // 信封态 Esc（拆封/收下等价——实现说明 A4）
  useEffect(() => {
    if (stage !== 'envelope') return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') dismissed('esc');
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [stage, dismissed]);

  // 锁背景滚动（组件在时恒锁）
  useEffect(() => {
    if (!notice) return;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = '';
    };
  }, [notice]);

  if (!checked || !notice) return null;

  const reduced =
    typeof window !== 'undefined' && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  // reduced-motion：信静置展开直接阅读态
  const effectiveStage: Stage = reduced && stage === 'envelope' ? 'reading' : stage;

  return (
    <div
      className='welcome-letter'
      role='dialog'
      aria-modal='true'
      aria-label={locale === 'zh-CN' ? '来自小镇的信' : 'A letter from the town'}
      ref={dialogRef}
      onClick={(e) => {
        // 信封态遮罩点击不关闭（仪式感，A3）；阅读态遮罩点击 = 收下
        if (effectiveStage === 'reading' && e.target === e.currentTarget) dismissed('button');
      }}
    >
      {effectiveStage !== 'reading' && (
        <div className={`wl-envelope-wrap${effectiveStage === 'unsealing' ? ' is-unsealing' : ''}`}>
          {/* 信封结构 1:1 照抄联络站 ContactPage（DOM 顺序/层级/材质/动画全部同源，
              仅 hover 触发改成 is-unsealing 状态；选择器 .wl-letter 隔离不碰 contact-page） */}
          <span className='wl-letter' aria-hidden='true'>
            <span className='envelope-scale wl-scale'>
              <span className='envelope'>
                <span className='letter-paper'>
                  <i></i>
                  <i></i>
                  <i></i>
                  <i></i>
                  <b>{locale === 'zh-CN' ? '来自小镇的信' : 'From the Town'}</b>
                </span>
                <span className='envelope-back'></span>
                <span className='envelope-flap'></span>
                <span className='letter-seal'>
                  <svg viewBox='0 0 40 40' role='img' aria-hidden='true'>
                    <circle cx='20' cy='20' r='17' fill='#6d3027' stroke='#d8bd82' strokeWidth='1.4' />
                    <text
                      x='20'
                      y='26'
                      textAnchor='middle'
                      fontFamily='var(--serif)'
                      fontSize='14'
                      fill='#d8bd82'
                      letterSpacing='1'
                    >
                      CS
                    </text>
                  </svg>
                </span>
              </span>
            </span>
          </span>
          <button
            type='button'
            className='button primary wl-unseal'
            ref={unsealBtnRef}
            autoFocus
            onClick={unseal}
          >
            {locale === 'zh-CN' ? '拆开这封信' : 'Open the letter'}
          </button>
        </div>
      )}

      {effectiveStage === 'reading' && (
        <article className={`paper-sheet is-open wl-sheet tone-${notice.tone}`}>
          <button
            type='button'
            className='paper-close'
            aria-label={locale === 'zh-CN' ? '收下这封信' : 'Put the letter away'}
            ref={closeBtnRef}
            onClick={() => dismissed('button')}
          />
          <p className='paper-date'>
            {new Date(notice.publishedAt).toLocaleDateString(locale === 'zh-CN' ? 'zh-CN' : 'en-US')}
          </p>
          <h2>{notice.title}</h2>
          {/* 服务端已净化（sanitize-html 白名单 + noopener）——同公告阅读层注入安全 */}
          <div dangerouslySetInnerHTML={{ __html: notice.html }} />
          <p className='paper-sign'>
            — {locale === 'zh-CN' ? '小镇记' : 'From the Town'} <span aria-hidden='true'>→</span>
          </p>
          <div className='wl-actions'>
            <Link className='button' to={`/${locale}/notices`} onClick={() => dismissed('button')}>
              {locale === 'zh-CN' ? '进公告栏看看' : 'Visit the Notice Board'}
            </Link>
            <button type='button' className='button' onClick={() => dismissed('button')}>
              {locale === 'zh-CN' ? '收下这封信' : 'Put the letter away'}
            </button>
          </div>
        </article>
      )}
    </div>
  );
}
